import { ChevronDown } from "lucide-react";
import {
  Button,
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  Form,
  FormSearchAddress,
  IconButton,
  Slider,
  useToast,
} from "@ac-expo/ui-components";
import { useForm } from "react-hook-form";
import { defaultPlaceValues, PlaceSchema } from "@ac-expo/api";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useGoogleApiContext } from "@web-app/src/context/googleApi.context";
import { observer } from "mobx-react-lite";
import filterStore from "@web-app/src/services/FilterStore";
import { TrashIcon } from "@web-app/src/icons";
import { useUserGeolocation } from "@web-app/src/context/userGeolocalisation.context";

interface SearchInputProps {
  setRadius: (value: number) => void;
  radius: number;
  maxRadius?: number;
  setLocationText?: (value?: string) => void;
}

export const SearchRadiusFilter = observer(
  ({
    setRadius,
    radius,
    maxRadius = 100,
    setLocationText,
  }: SearchInputProps) => {
    const { toast } = useToast();
    const { location: userGeolocation } = useUserGeolocation();
    const location = filterStore.location;
    const { isLoaded } = useGoogleApiContext();

    const form = useForm<PlaceSchema>({
      defaultValues: {
        ...defaultPlaceValues,
        address: {
          ...defaultPlaceValues.address,
          lat: location?.lat,
          long: location?.lng,
        },
      },
      mode: "all",
    });
    const { control, watch, reset, setValue } = form;

    const handleSubmit = (e: React.FormEvent) => {
      e.preventDefault();
    };

    const address = watch("address");

    useEffect(() => {
      const { lat, long } = address;
      if (!location && (lat !== null || long !== null)) {
        reset();
        setRadius(20);
      }
    }, [location, reset]);

    const handleResetFilters = () => {
      filterStore.setLocation(null);
      filterStore.setLocationText("");
      filterStore.setLocationFilter(null, null, null);
      reset();
      setRadius(20);
    };

    const onUseMyLocation = () => {
      if (userGeolocation === null) {
        filterStore.setLoading(false);
        toast({
          title: "Erreur",
          description: `Veuillez autoriser l'accès à votre position.`,
        });
        return;
      }

      filterStore.setLoading(true);
      setValue("address", defaultPlaceValues.address);
      filterStore.setLocationText("Ma localisation");

      const { latitude: lat, longitude: lng } = userGeolocation;
      const location = { lat, lng };
      filterStore.setLocation(location);
      filterStore.setLoading(false);
      filterStore.setLocationFilter(null, null, null);
    };

    return (
      <div className="flex w-full flex-row items-center gap-2">
        <form onSubmit={handleSubmit} className="w-full">
          <Form {...form}>
            <div className="relative flex h-[41px] w-full grow">
              <FormSearchAddress
                control={control}
                name="address"
                placeholder={filterStore.locationText || "Lieu"}
                isMapApiLoaded={isLoaded}
                className={cn(
                  "h-[41px] grow justify-center gap-0 overflow-hidden text-ellipsis font-normal text-gray-500",
                  {
                    "w-[calc(100%-46px)]": radius === 100,
                    "w-[calc(100%-40px)]":
                      radius && radius < 100 && radius >= 10,
                    "w-[calc(100%-34px)]": radius && radius < 10 && radius > 0,
                    "placeholder:text-black":
                      filterStore.locationText === "Ma localisation",
                  }
                )}
                setValue={setLocationText}
                setLocationFilter={(zipCode, city, country) => {
                  // Store the location filter data temporarily
                  filterStore.setLocationFilter(zipCode, city, country);
                }}
                setLocationAndFilter={(
                  location,
                  locationText,
                  zipCode,
                  city,
                  country
                ) => {
                  filterStore.setLocationAndFilter(
                    location,
                    locationText,
                    zipCode,
                    city,
                    country
                  );
                }}
              />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className={cn(
                      "absolute right-0 mt-[2.5px] h-[36px] rounded-l-none border bg-white px-3 py-2 text-sm font-medium transition-colors disabled:pointer-events-none disabled:opacity-50"
                    )}
                  >
                    {radius !== null ? `${radius} km` : null}
                    <ChevronDown
                      className={cn("size-4", {
                        "ml-2": radius,
                      })}
                    />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-72">
                  <div className="flex flex-col gap-2 p-4">
                    <div className="mb-2 flex items-center gap-2">
                      <h3 className="font-medium">
                        Dans un rayon de {radius} km
                      </h3>
                    </div>
                    <Slider
                      value={radius ? [radius] : [0]}
                      onValueChange={(value) => setRadius(value[0])}
                      max={maxRadius}
                      step={5}
                      className="mb-2"
                    />
                    <div className="flex justify-between text-sm text-muted-foreground">
                      <span>1 km</span>
                      <span>{radius} km</span>
                      <span>{maxRadius} km</span>
                    </div>
                    <IconButton
                      svgType="paperPlaneTilt"
                      svgProps={{ size: 24 }}
                      variant={"outline-blue"}
                      onClick={onUseMyLocation}
                    >
                      Utiliser ma position actuelle
                    </IconButton>
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </Form>
        </form>
        {(filterStore.location ||
          filterStore.locationText ||
          radius !== 20) && (
          <Button
            onClick={handleResetFilters}
            variant="ghost"
            aria-label="Réinitialiser les filtres"
            className="self-center p-0 px-1"
          >
            <TrashIcon height={15} width={15} fill={"#344054"} />
          </Button>
        )}
      </div>
    );
  }
);
